import {
  BranchItemFragment,
  OnlineOrderAvailabilityItemFragment,
  OnlineOrderBranchConfigurationItemFragment,
  OnlineOrderDeliveryMode,
  OnlineOrderItemFragment,
  OnlineOrderUserState,
  OnlineOrderPaymentMethod,
  HappyHourMode,
} from '@app/graphql/types/graphql.ts';
import { CartSideBarPreviewMode } from '@app/page/online-order/fill-contact-info/utils/cart-side-bar-preview-mode-enum.ts';
import CartSideBarPreview from '@app/page/online-order/fill-contact-info/component/cart-side-bar-preview.tsx';
import { useEffect, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { CardSelection, CardSelectionData } from '@app/components/card-selection.tsx';
import { useOnlineOrderFillPersonalData } from '@app/page/online-order/fill-contact-info/logic/use-online-order-fill-personal-contact.ts';
import { formatGraphQlError, validationErrors } from '@app/module/error/error.ts';
import { captureException } from '@sentry/react';
import useFormData from '@app/utils/use-form-data.ts';
import UnifiedDeliveryContactCard from '@app/page/online-order/fill-contact-info/component/unified-delivery-contact-card.tsx';
import Card from '@app/components/card/card.tsx';
import CardHeader from '@app/components/card/card-header.tsx';
import SuperButton from '@app/components/super-button.tsx';
import OrderTime from '@app/page/online-order/fill-contact-info/component/order-time.tsx';
import Spinner from '@app/components/spinner.tsx';
import AlertError from '@app/module/error/alert-error';
import { formatCurrency } from '@app/components/price/currency-formatter';
import { deliveryDiscountAdditionalText, pickupDiscountAdditionalText } from './utils/pickup-discount-additional-text';
import HappyHourBanner from '../product-picker/component/happy-hour-banner';
import useReloadDetection from '@app/utils/use-reload-detection.ts';
import ReloadConfirmationDialog from '@app/components/reload-confirmation-dialog.tsx';
import ErrorDialog from '@app/page/online-order/fill-contact-info/component/error-dialog.tsx';
import { mapErrorToDialog } from '@app/page/online-order/fill-contact-info/utils/map-error-to-dialog.ts';
import { ApolloError } from '@apollo/client';

interface FormData {
  streetAddress: string;
  streetAddress2: string;
  addressLocality: string;
  addressRegion: string;
  postalCode: string;
  addressCountry: string;

  guestName: string;
  email: string;
  phone: string;
  message: string;
  expectedTime: string | undefined;
  paymentMethod: OnlineOrderPaymentMethod;
}

export default function FillContactInfoPage(props: {
  branch: BranchItemFragment;
  order: OnlineOrderItemFragment;
  onlineOrderBranchConfiguration: OnlineOrderBranchConfigurationItemFragment;
  availabilityData: OnlineOrderAvailabilityItemFragment;
}) {
  const { t } = useTranslate();
  const currency = props.branch.company.settings.currency;

  // Reload detection hook
  const {
    hasUnsavedChanges,
    showDialog,
    lastChangeTimestamp,
    markAsUnsaved,
    markAsSaved,
    showConfirmationDialog,
    hideConfirmationDialog,
  } = useReloadDetection({
    storageKey: `fill_contact_info_${props.order.id}`,
    warningMessage: 'Bạn có chắc chắn muốn rời khỏi trang? Thông tin liên hệ chưa được lưu sẽ bị mất.',
    enableManualTrigger: true,
  });

  const deliveryModes: CardSelectionData<OnlineOrderDeliveryMode>[] = useMemo(() => {
    return [
      {
        id: OnlineOrderDeliveryMode.Delivery,
        value: OnlineOrderDeliveryMode.Delivery,
        title: t('online-order.fill-contact-page.form-input.delivery-mode.card-selection.delivery.title', 'Delivery'),
        description: t(
          'online-order.fill-contact-page.form-input.delivery-mode.card-selection.delivery.description',
          'We will deliver the order to your address'
        ),
        additionalText: [
          t(
            'online-order.fill-contact-page.form-input.delivery-mode.card-selection.delivery.addition-text',
            'Minimum order amount for delivery: '
          ) + ` ${formatCurrency(props.onlineOrderBranchConfiguration.minimumOrderAmountForDelivery, currency)}`,
          deliveryDiscountAdditionalText(
            props.onlineOrderBranchConfiguration.deliveryDiscountAmount,
            props.onlineOrderBranchConfiguration.deliveryDiscountType,
            currency
          ),
        ],
      },
      {
        id: OnlineOrderDeliveryMode.Pickup,
        value: OnlineOrderDeliveryMode.Pickup,
        title: t('online-order.fill-contact-page.form-input.delivery-mode.card-selection.pickup.title', 'Pickup'),
        description: t(
          'online-order.fill-contact-page.form-input.delivery-mode.card-selection.pickup.description',
          'You will pick up the order at the store'
        ),
        additionalText: [
          pickupDiscountAdditionalText(
            props.onlineOrderBranchConfiguration.pickupDiscountAmount,
            props.onlineOrderBranchConfiguration.pickupDiscountType,
            currency
          ),
        ],
      },
    ];
  }, [
    currency,
    props.onlineOrderBranchConfiguration.deliveryDiscountAmount,
    props.onlineOrderBranchConfiguration.deliveryDiscountType,
    props.onlineOrderBranchConfiguration.minimumOrderAmountForDelivery,
    props.onlineOrderBranchConfiguration.pickupDiscountAmount,
    props.onlineOrderBranchConfiguration.pickupDiscountType,
    t,
  ]);

  let initialDeliveryMode: CardSelectionData<OnlineOrderDeliveryMode> =
    deliveryModes.length > 0 ? deliveryModes[0] : deliveryModes[1];

  if (props.order.deliveryMode) {
    initialDeliveryMode = deliveryModes.find((mode) => mode.value === props.order.deliveryMode) ?? deliveryModes[0];
  }

  // Hooks
  const [selectedDeliveryMode, setSelectedDeliveryMode] = useState(initialDeliveryMode);

  const handleChangeDeliveryMode = (newDeliveryMode: CardSelectionData<OnlineOrderDeliveryMode>) => {
    setSelectedDeliveryMode(newDeliveryMode);
    markAsUnsaved();
  };

  const [errorDialog, setErrorDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    actionText?: string;
    focusField?: string | null;
    contactPhone?: string | null;
  } | null>(null);

  const [changeUserState, { loading, error }] = useOnlineOrderFillPersonalData();

  const paymentMethods: CardSelectionData<OnlineOrderPaymentMethod>[] = useMemo(() => {
    const selectionPayment = [
      {
        id: 'CASH' as OnlineOrderPaymentMethod,
        value: 'CASH' as OnlineOrderPaymentMethod,
        title: t('online-order.fill-contact-page.form-input.payment-method.card-selection.cash.title', 'Cash'),
        description: t(
          'online-order.fill-contact-page.form-input.payment-method.card-selection.cash.description',
          'Pay with cash upon delivery or pickup'
        ),
        additionalText: [],
      },
      {
        id: 'CARD_AT_PLACE' as OnlineOrderPaymentMethod,
        value: 'CARD_AT_PLACE' as OnlineOrderPaymentMethod,
        title: t(
          'online-order.fill-contact-page.form-input.payment-method.card-selection.card-at-place.title',
          'Card on site'
        ),
        description: t(
          'online-order.fill-contact-page.form-input.payment-method.card-selection.card-at-place.description',
          'Pay by card in person upon delivery or pickup'
        ),
        additionalText: [],
      },
      {
        id: 'STRIPE' as OnlineOrderPaymentMethod,
        value: 'STRIPE' as OnlineOrderPaymentMethod,
        title: t('online-order.fill-contact-page.form-input.payment-method.card-selection.stripe.title', 'Card'),
        description: t(
          'online-order.fill-contact-page.form-input.payment-method.card-selection.stripe.description',
          'Pay securely with your credit or debit card'
        ),
        additionalText: [],
      },
      {
        id: 'MANUAL_BANK_TRANSFER' as OnlineOrderPaymentMethod,
        value: 'MANUAL_BANK_TRANSFER' as OnlineOrderPaymentMethod,
        title: t(
          'online-order.fill-contact-page.form-input.payment-method.card-selection.online-payment.title',
          'Online Payment'
        ),
        description: t(
          'online-order.fill-contact-page.form-input.payment-method.card-selection.online-payment.description',
          'Pay using one of our online payment options, then upload your payment screenshot'
        ),
        additionalText: [],
      },
    ];

    return selectionPayment.filter((item) => props.onlineOrderBranchConfiguration.paymentMethods.includes(item.id));
  }, [props.onlineOrderBranchConfiguration.paymentMethods, t]);

  const handleChangePaymentMethod = (newPaymentMethod: CardSelectionData<OnlineOrderPaymentMethod>) => {
    manualHandle('paymentMethod', newPaymentMethod.value);
    markAsUnsaved();
  };

  const {
    data: form,
    handle,
    manualHandle,
  } = useFormData<FormData>({
    streetAddress: props.order.streetAddress ?? '',
    streetAddress2: props.order.streetAddress2 ?? '',
    addressLocality: props.order.addressLocality ?? '',
    addressRegion: props.order.addressRegion ?? '',
    postalCode: props.order.postalCode ?? '',
    addressCountry: props.order.addressCountry ?? '',

    guestName: props.order.guestName ?? '',
    email: props.order.email ?? '',
    phone: props.order.phone ?? '',
    message: props.order.message ?? '',
    expectedTime: props.order.expectedTime ?? '',
    paymentMethod: props.order.paymentMethod ?? OnlineOrderPaymentMethod.Cash,
  });

  // Mark as unsaved when form data changes
  useEffect(() => {
    if (form.guestName || form.email || form.phone || form.streetAddress) {
      markAsUnsaved();
    }
  }, [form, markAsUnsaved]);

  const [emptyErrors, setEmptyErrors] = useState<Record<string, string>>({});
  const [scheduleMode, setScheduleMode] = useState<'order_for_later' | 'now'>();

  const applicationErrors = formatGraphQlError(error?.graphQLErrors);
  const validationError = validationErrors(applicationErrors);

  let availableDeliveryModes: CardSelectionData<OnlineOrderDeliveryMode>[] = [];

  if (props.onlineOrderBranchConfiguration.deliveryEnable) {
    availableDeliveryModes.push(deliveryModes[0]);
  }

  if (props.onlineOrderBranchConfiguration.pickupEnable) {
    availableDeliveryModes.push(deliveryModes[1]);
  }

  if (props.onlineOrderBranchConfiguration.happyHourMode == HappyHourMode.Advanced) {
    availableDeliveryModes = availableDeliveryModes.map((item) => ({
      ...item,
      additionalText: [],
    }));
  }

  useEffect(() => {
    let updateDeliveryMode: CardSelectionData<OnlineOrderDeliveryMode> = deliveryModes[0];

    if (props.order.deliveryMode == OnlineOrderDeliveryMode.Pickup) {
      updateDeliveryMode = deliveryModes[1];
    }

    if (props.onlineOrderBranchConfiguration.minimumOrderAmountForDelivery > (props.order.basePrice ?? 0)) {
      updateDeliveryMode = deliveryModes[1];
    }

    setSelectedDeliveryMode(updateDeliveryMode);
  }, [
    deliveryModes,
    setSelectedDeliveryMode,
    props.availabilityData.delivery.now,
    props.onlineOrderBranchConfiguration.minimumOrderAmountForDelivery,
    props.order.basePrice,
    props.order.deliveryMode,
  ]);

  const validateEmpty = () => {
    let flag = true;
    const errors: Record<string, string> = {};
    if (form.guestName == '') {
      flag = false;
      errors.guestName = t('online-order.information.empty.guestName', 'Guest name should not be empty');
    }
    if (form.email == '') {
      flag = false;
      errors.email = t('online-order.information.empty.email', 'Email should not be empty');
    }
    if (form.phone == '') {
      flag = false;
      errors.phone = t('online-order.information.empty.phone', 'Phone name should not be empty');
    }
    if (form.streetAddress == '' && selectedDeliveryMode.value === OnlineOrderDeliveryMode.Delivery) {
      flag = false;
      errors.streetAddress = t('online-order.information.empty.streetAddress', 'Street address should not be empty');
    }
    if (form.addressLocality == '' && selectedDeliveryMode.value === OnlineOrderDeliveryMode.Delivery) {
      flag = false;
      errors.addressLocality = emptyErrors.streetAddress = t(
        'online-order.information.empty.addressLocality',
        'City should not be empty'
      );
    }
    if (form.postalCode == '' && selectedDeliveryMode.value === OnlineOrderDeliveryMode.Delivery) {
      flag = false;
      errors.postalCode = t(
        'online-order.fill-contact-page.form-input.delivery-mode.card-selection.delivery.addition-text',
        'Postal code should not be empty'
      );
    }

    setEmptyErrors(errors);
    return flag;
  };

  const saveAddressAndChangeState = (nextUserState: OnlineOrderUserState) => {
    if (!validateEmpty()) {
      return;
    }
    changeUserState({
      variables: {
        onlineOrderID: props.order.id,
        input: {
          deliveryMode: selectedDeliveryMode.value,
          paymentMethod: form.paymentMethod,

          streetAddress: form.streetAddress,
          streetAddress2: form.streetAddress2,
          addressLocality: form.addressLocality,
          addressRegion: form.addressRegion,
          postalCode: form.postalCode,
          addressCountry: form.addressCountry,

          guestName: form.guestName,
          email: form.email,
          phone: form.phone,
          message: form.message,

          nextUserState: nextUserState,

          ...(form.expectedTime && { expectedTime: form.expectedTime }),
        },
      },
    })
      .then(() => {
        markAsSaved();
      })
      .catch((error: ApolloError) => {
        captureException(error); // Still log to Sentry

        // Show error dialog to user
        const dialogData = mapErrorToDialog(error, t, props.branch.contactPhone);
        setErrorDialog({
          isOpen: true,
          ...dialogData,
        });
      });
  };

  const backToEditCartStateHandler = () => {
    if (hasUnsavedChanges) {
      showConfirmationDialog();
    } else {
      changeUserState({
        variables: {
          onlineOrderID: props.order.id,
          input: {
            deliveryMode: selectedDeliveryMode.value,
            paymentMethod: form.paymentMethod,

            streetAddress: props.order.streetAddress ?? '',
            streetAddress2: props.order.streetAddress2 ?? '',
            addressLocality: props.order.addressLocality ?? '',
            addressRegion: props.order.addressRegion ?? '',
            postalCode: props.order.postalCode ?? '',
            addressCountry: props.order.addressCountry ?? '',

            guestName: props.order.guestName ?? '',
            email: props.order.email ?? '',
            phone: props.order.phone ?? '',
            message: props.order.message,

            nextUserState: OnlineOrderUserState.PickingProduct,
          },
        },
      }).catch(captureException);
    }
  };

  // Custom confirm reload function that includes changeUserState
  const handleConfirmReload = () => {
    hideConfirmationDialog();
    markAsSaved();

    // Thực hiện changeUserState để quay lại trang trước
    changeUserState({
      variables: {
        onlineOrderID: props.order.id,
        input: {
          deliveryMode: selectedDeliveryMode.value,
          paymentMethod: form.paymentMethod,

          streetAddress: props.order.streetAddress ?? '',
          streetAddress2: props.order.streetAddress2 ?? '',
          addressLocality: props.order.addressLocality ?? '',
          addressRegion: props.order.addressRegion ?? '',
          postalCode: props.order.postalCode ?? '',
          addressCountry: props.order.addressCountry ?? '',

          guestName: props.order.guestName ?? '',
          email: props.order.email ?? '',
          phone: props.order.phone ?? '',
          message: props.order.message,

          nextUserState: OnlineOrderUserState.PickingProduct,
        },
      },
    }).catch(captureException);
  };

  const reviewDisable = () => {
    if (loading) {
      return true;
    }

    if (selectedDeliveryMode.value === OnlineOrderDeliveryMode.Delivery) {
      if (props.onlineOrderBranchConfiguration.minimumOrderAmountForDelivery > (props.order.basePrice ?? 0)) {
        return true;
      }
    }

    return scheduleMode == 'order_for_later' && !form.expectedTime;
  };

  const reviewHandler = () => {
    saveAddressAndChangeState(OnlineOrderUserState.Review);
  };

  return (
    <>
      <div className="contact-info-page flex items-center justify-center sm:p-8">
        <div className="contact-info-page__container max-w-[1000px] grow space-y-4 sm:basis-2/3">
          {props.onlineOrderBranchConfiguration.paymentMethods.length == 0 && (
            <AlertError
              title={t('empty-payment-method-error')}
              message={t('empty-payment-method-error-message', 'Payment method is not available')}
            />
          )}

          <HappyHourBanner
            branchId={props.branch.id}
            currency={props.branch.company.settings.currency}
            configuration={props.onlineOrderBranchConfiguration}
          />

          <BackStateCard
            loading={loading}
            backToEditCartStateHandler={backToEditCartStateHandler}
            deliveryMode={selectedDeliveryMode.value}
            t={(key: string, defaultValue: string) => {
              return t(key, defaultValue);
            }}
          />
          <div className="contact-info-page__sidebar">
            <CartSideBarPreview
              order={props.order}
              onlineOrderBranchConfiguration={props.onlineOrderBranchConfiguration}
              deliveryMode={selectedDeliveryMode.value}
              mode={CartSideBarPreviewMode.OnlyProducts}
            />
          </div>
          <div className="contact-info-page__delivery-options">
            <UnifiedDeliveryContactCard
              deliveryModes={availableDeliveryModes}
              selectedDeliveryMode={selectedDeliveryMode}
              onDeliveryModeChange={handleChangeDeliveryMode}
              disableDeliveryMode={
                props.onlineOrderBranchConfiguration.minimumOrderAmountForDelivery > (props.order.basePrice ?? 0)
              }
              values={{
                streetAddress: form.streetAddress,
                streetAddress2: form.streetAddress2,
                addressLocality: form.addressLocality,
                postalCode: form.postalCode,
                guestName: form.guestName,
                email: form.email,
                phone: form.phone,
                message: form.message,
              }}
              onChange={handle}
              manualHandle={(name: string, value: unknown) => {
                // @ts-expect-error accept for every type
                manualHandle(name as keyof FormData, value);
                markAsUnsaved();
              }}
              errors={{ ...validationError, ...emptyErrors }}
              branch={props.branch}
            />
          </div>
          {props.onlineOrderBranchConfiguration.paymentMethods.length > 0 && (
            <CardSelection
              title={t('online-order.fill-contact-page.form-input.payment-method.label', 'Payment method')}
              data={paymentMethods}
              value={paymentMethods.find((method) => method.value === form.paymentMethod) ?? paymentMethods[0]}
              onChange={handleChangePaymentMethod}
              disableDeliveryMode={false}
            />
          )}
          <OrderTime
            availabilityData={props.availabilityData}
            branch={props.branch}
            setParentSceduleMode={(mode: 'order_for_later' | 'now') => {
              setScheduleMode(mode);
            }}
            onlineOrderBranchConfiguration={props.onlineOrderBranchConfiguration}
            deliveryMode={selectedDeliveryMode.value}
            expectedTime={form.expectedTime}
            handleExpectedTime={(expectedTime: string | undefined) => {
              manualHandle('expectedTime', expectedTime);
              markAsUnsaved();
            }}
            timezone={props.branch.company.settings.timezone}
          />
          <div className="contact-info-page__actions px-1 sm:px-0">
            <SuperButton disabled={reviewDisable()} onClick={reviewHandler}>
              {loading ? <Spinner /> : <span>Next</span>}
            </SuperButton>
          </div>
        </div>
      </div>

      {/* Reload Confirmation Dialog */}
      <ReloadConfirmationDialog
        isOpen={showDialog}
        onClose={hideConfirmationDialog}
        onConfirm={handleConfirmReload}
        title={t('reload-confirmation-dialog.back-confirm.title', 'Xác nhận quay lại')}
        message={t(
          'reload-confirmation-dialog.back-confirm.message',
          'Bạn có chắc chắn muốn quay lại trang trước? Những thay đổi chưa được lưu sẽ bị mất.'
        )}
        confirmText={t('reload-confirmation-dialog.back-confirm.confirm', 'Quay lại')}
        cancelText={t('reload-confirmation-dialog.back-confirm.cancel', 'Ở lại')}
        showTimestamp={true}
        timestamp={lastChangeTimestamp || undefined}
      />

      {/* Error Dialog */}
      {errorDialog?.isOpen && (
        <ErrorDialog
          isOpen={errorDialog.isOpen}
          onClose={() => setErrorDialog(null)}
          error={{
            title: errorDialog.title,
            message: errorDialog.message,
            actionText: errorDialog.actionText,
            contactPhone: errorDialog.contactPhone,
            onAction: errorDialog.focusField
              ? () => {
                  setErrorDialog(null);
                  // Focus on problematic field
                  const element = document.querySelector(`input[name="${errorDialog.focusField}"]`);
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    (element as HTMLInputElement).focus();
                  }
                }
              : undefined,
          }}
        />
      )}
    </>
  );
}

const BackStateCard = (props: {
  backToEditCartStateHandler: () => void;
  deliveryMode: OnlineOrderDeliveryMode;
  t: (key: string, defaultValue: string) => string;
  loading: boolean;
}) => {
  const isPickup = props.deliveryMode === OnlineOrderDeliveryMode.Pickup;
  const badgeText = isPickup
    ? props.t('online-order.fill-contact-page.form-input.delivery-mode.card-selection.pickup.title', 'Pickup')
    : props.t('online-order.fill-contact-page.form-input.delivery-mode.card-selection.delivery.title', 'Delivery');

  return (
    <Card className="card sticky top-0 z-10 sm:relative">
      <CardHeader
        title={!props.loading ? props.t('online-order.review-page.back-to-edit-address', 'Back to edit address') : ''}
        handleBackButtonClick={props.backToEditCartStateHandler}
        withBackButton={true}
      >
        <div className="flex items-center gap-2">
          <span
            className={`delivery-mode-badge inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
              isPickup
                ? 'bg-blue-100 text-blue-800 border border-blue-300'
                : 'bg-green-100 text-green-800 border border-green-300'
            }`}
          >
            {badgeText}
          </span>
          {props.loading && <Spinner />}
        </div>
      </CardHeader>
    </Card>
  );
};
